import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../shared/Context/auth-context';
import PageHeading from '../shared/components/UIElements/PageHeading';
import Icon from '../shared/components/UIElements/Icon';
import { useCareMutation, useCareResource } from './care-api';
import '../shared/components/FormElements/Button.css';
import './Care.css';
import './CareInbox.css';

const scopeLabels = {
  timeline: 'Clinical history',
  vitals: 'Vitals',
  reports: 'Reports',
  updates: 'Updates and conversations',
  medications: 'Medications'
};
const entryLabels = {
  vital: 'Vital reading',
  symptom: 'Symptom update',
  question: 'Patient question',
  event: 'Clinical event',
  pharmacy: 'Pharmacy update',
  report: 'Report'
};
const dateLabel = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
const patientPath = (id) => `/care/patients/${encodeURIComponent(id)}`;

const CareInbox = () => {
  const auth = useContext(AuthContext);
  const pharmacy = Boolean(auth.isPharmacyLoggedIn);
  const resource = useCareResource('/inbox');
  const mutation = useCareMutation();
  const [notice, setNotice] = useState('');
  const requests = Array.isArray(resource.data?.requests) ? resource.data.requests : [];
  const updates = Array.isArray(resource.data?.updates) ? resource.data.updates : [];
  const patients = Array.isArray(resource.data?.patients) ? resource.data.patients : [];
  const respond = async (request, action) => {
    setNotice('');
    mutation.clearError();
    try {
      await mutation.mutate(
        `/patients/${encodeURIComponent(request.patientId)}/members/${encodeURIComponent(request.id)}`,
        'PATCH',
        { action }
      );
      setNotice(
        action === 'accept'
          ? `Request from ${request.patientName} accepted. Their shared information is now available.`
          : `Request from ${request.patientName} declined.`
      );
      await resource.reload();
    } catch {
      /* The hook shows the error; never remove an unconfirmed request. */
    }
  };
  return (
    <div className="care-inbox">
      <PageHeading
        eyebrow="CARE, CONNECTED"
        title={pharmacy ? 'Shared patient care' : 'Care inbox'}
        description={
          pharmacy
            ? 'Stay connected to the patients who share their care with your pharmacy.'
            : 'Review patient updates, respond in context, and keep follow-ups moving.'
        }
      >
        <button
          type="button"
          className="button button--inverse"
          onClick={resource.reload}
          disabled={resource.loading || mutation.saving}
        >
          Refresh
        </button>
      </PageHeading>
      {notice && (
        <div className="care-notice" role="status">
          {notice}
        </div>
      )}
      {mutation.error && (
        <div className="care-error" role="alert">
          {mutation.error}
        </div>
      )}
      {resource.error && (
        <div className="care-error" role="alert">
          <p>{resource.error}</p>
          <button type="button" className="button button--inverse" onClick={resource.reload}>
            Retry loading inbox
          </button>
        </div>
      )}
      {resource.loading && (
        <p className="care-muted" role="status">
          Loading shared care…
        </p>
      )}
      {resource.data && !resource.error && (
        <>
          <div className="care-inbox-summary" aria-label="Care inbox summary">
            {!pharmacy && (
              <a href="#care-awaiting-review" className="care-inbox-stat">
                <span className="care-inbox-stat__icon">
                  <Icon name="file" size={23} />
                </span>
                <span>
                  <strong>{updates.length}</strong>
                  <span>Assigned updates</span>
                </span>
              </a>
            )}
            <a href="#care-sharing-requests" className="care-inbox-stat">
              <span className="care-inbox-stat__icon">
                <Icon name="patient" size={23} />
              </span>
              <span>
                <strong>{requests.length}</strong>
                <span>Sharing requests</span>
              </span>
            </a>
            <a href="#care-linked-patients" className="care-inbox-stat">
              <span className="care-inbox-stat__icon">
                <Icon name="heart" size={23} />
              </span>
              <span>
                <strong>{patients.length}</strong>
                <span>Shared patients</span>
              </span>
            </a>
          </div>
          <div className="care-inbox-layout">
            <div>
              {!pharmacy && (
                <section
                  className="care-panel"
                  id="care-awaiting-review"
                  aria-labelledby="care-awaiting-title"
                >
                  <div className="care-inbox-section-heading">
                    <h2 id="care-awaiting-title">Assigned to you</h2>
                    <span className="care-pill">{updates.length}</span>
                  </div>
                  <p className="care-muted">
                    Each update keeps its named reviewer until the patient explicitly reassigns it.
                  </p>
                  {!updates.length ? (
                    <div className="care-empty">
                      <Icon name="check" size={30} />
                      <h3>No updates awaiting your review</h3>
                      <p>Updates patients send to you will appear here.</p>
                    </div>
                  ) : (
                    <ul className="care-inbox-list">
                      {updates.map((entry) => (
                        <li key={entry.id} className="care-inbox-update">
                          <div className="care-inbox-update__meta">
                            <span>{entryLabels[entry.type] || 'Patient update'}</span>
                            <span className="care-pill">
                              {entry.status === 'needs-information'
                                ? 'Awaiting patient information'
                                : 'Awaiting your review'}
                            </span>
                          </div>
                          <h3>
                            <Link
                              to={`${patientPath(entry.patientId)}?entry=${encodeURIComponent(entry.id)}`}
                            >
                              {entry.title || entryLabels[entry.type] || 'Patient update'}
                            </Link>
                          </h3>
                          <p className="care-inbox-patient-name">{entry.patientName}</p>
                          {entry.body && <p className="care-inbox-excerpt">{entry.body}</p>}
                          <div className="care-inbox-update__footer">
                            <span className="care-muted">
                              {dateLabel(entry.occurredAt || entry.createdAt)}
                              {entry.author?.name ? ` · ${entry.author.name}` : ''}
                            </span>
                            <Link
                              className="care-inbox-review-link"
                              to={`${patientPath(entry.patientId)}?entry=${encodeURIComponent(entry.id)}`}
                            >
                              Open update <Icon name="arrow" size={16} />
                            </Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}
              <section
                className="care-panel"
                id="care-sharing-requests"
                aria-labelledby="care-requests-title"
              >
                <div className="care-inbox-section-heading">
                  <h2 id="care-requests-title">Sharing requests</h2>
                  <span className="care-pill">{requests.length}</span>
                </div>
                <p className="care-muted">
                  Patients choose what they share. Accept a request to access that information.
                </p>
                {!requests.length ? (
                  <div className="care-empty">
                    <p>No pending sharing requests.</p>
                  </div>
                ) : (
                  <ul className="care-inbox-list">
                    {requests.map((request) => {
                      const expired =
                        request.expiresAt && new Date(request.expiresAt) <= new Date();
                      return (
                        <li key={request.id} className="care-inbox-request">
                          <div className="care-inbox-section-heading">
                            <h3>{request.patientName}</h3>
                            <span className="care-pill">
                              {expired
                                ? 'Expired'
                                : request.primary
                                  ? 'Primary reviewer requested'
                                  : 'Shared access'}
                            </span>
                          </div>
                          <p>
                            <strong>Patient is sharing:</strong>{' '}
                            {(request.scopes || [])
                              .map((scope) => scopeLabels[scope] || scope)
                              .join(', ') || 'No categories listed'}
                          </p>
                          {request.primary ? (
                            <p className="care-notice">
                              Accepting makes you this patient’s primary reviewer for future
                              updates. Existing review assignments are not transferred
                              automatically.
                            </p>
                          ) : (
                            <p className="care-muted">
                              Access does not make you the primary reviewer.
                            </p>
                          )}
                          <p className="care-muted">
                            {request.expiresAt
                              ? `Access expires ${dateLabel(request.expiresAt)}`
                              : 'Access has no expiry date. The patient can remove it.'}
                          </p>
                          <div className="care-actions">
                            <button
                              type="button"
                              className="button"
                              disabled={Boolean(expired) || mutation.saving || resource.loading}
                              onClick={() => respond(request, 'accept')}
                            >
                              Accept request
                            </button>
                            <button
                              type="button"
                              className="button button--inverse"
                              disabled={mutation.saving || resource.loading}
                              onClick={() => respond(request, 'decline')}
                            >
                              Decline
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
            <section
              className="care-panel care-inbox-patients"
              id="care-linked-patients"
              aria-labelledby="care-patients-title"
            >
              <h2 id="care-patients-title">Shared patients</h2>
              <p className="care-muted">
                Open a record to see the information that patient has shared with you.
              </p>
              {!patients.length ? (
                <div className="care-empty">
                  <Icon name="patient" size={30} />
                  <p>Accepted sharing requests will add patients here.</p>
                </div>
              ) : (
                <ul className="care-inbox-list">
                  {patients.map((patient) => (
                    <li key={patient.id}>
                      <Link className="care-inbox-patient" to={patientPath(patient.id)}>
                        <span className="care-inbox-patient__avatar">
                          <Icon name="patient" size={20} />
                        </span>
                        <span>{patient.name}</span>
                        <Icon name="arrow" size={18} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default CareInbox;
